import { translations, type Lang } from "../../i18n";
import Logo from "./Logo";

type Translation = (typeof translations)["de"];

function Footer({
  navigate,
  t,
  theme = "dark",
  lang = "de",
}: {
  navigate?: (p: string) => void;
  t: Translation["footer"];
  theme?: "dark" | "light";
  lang?: Lang;
}) {
  // Use the German legal-page URLs for German visitors (Impressum,
  // Datenschutzerklärung) — those paths match what German users and search
  // engines expect and are aliased in App.tsx to the same components.
  const imprintPath = lang === "de" ? "/impressum" : "/imprint";
  const dataSecurityPath = lang === "de" ? "/datenschutz" : "/data-security";
  const go = (e: React.MouseEvent, path: string) => {
    if (!navigate) return;
    e.preventDefault();
    navigate(path);
  };

  const linkHref = (path: string) => {
    // Support deployments under a sub-path (e.g. GitHub Pages project sites).
    // Vite exposes this as BASE_URL when `base` is configured.
    const basePath =
      import.meta.env.BASE_URL && import.meta.env.BASE_URL !== "/"
        ? import.meta.env.BASE_URL.replace(/\/$/, "")
        : "";
    const p = path.startsWith("/") ? path : `/${path}`;
    return basePath ? `${basePath}${p}` : p;
  };

  const isDark = theme === "dark";

  // Theme-based styles
  const footerBg = isDark ? "bg-slate-950" : "bg-slate-900";
  const borderColor = isDark ? "border-slate-800" : "border-slate-800";
  const headingColor = isDark ? "text-white" : "text-white";
  const textColor = isDark ? "text-slate-400" : "text-slate-400";
  const hoverColor = isDark ? "hover:text-white" : "hover:text-orange-400";
  const mutedColor = isDark ? "text-slate-500" : "text-slate-500";

  return (
    <footer className={`border-t ${borderColor} py-16 ${footerBg}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <Logo size="sm" theme="dark" />
            <p className={`mt-4 ${textColor}`}>
              {t.productDesc}
            </p>
          </div>
          <div>
            <h3 className={`font-semibold mb-4 ${headingColor}`}>{t.productHeading}</h3>
            <ul className={`space-y-2 ${textColor}`}>
              <li>
                <a href="/#wie" className={`${hoverColor} transition-colors`}>
                  {t.how}
                </a>
              </li>
              <li>
                <a
                  href="/#pricing"
                  className={`${hoverColor} transition-colors`}
                >
                  {t.pricing}
                </a>
              </li>
              <li>
                <a href="/blogs" className={`${hoverColor} transition-colors`}>
                  {t.blog}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className={`font-semibold mb-4 ${headingColor}`}>{t.legalHeading}</h3>
            <ul className={`space-y-2 ${textColor}`}>
              <li>
                <a
                  href={linkHref(dataSecurityPath)}
                  onClick={(e) => go(e, dataSecurityPath)}
                  className={`${hoverColor} transition-colors`}
                >
                  {t.dataSecurity}
                </a>
              </li>
              <li>
                <a
                  href={linkHref(imprintPath)}
                  onClick={(e) => go(e, imprintPath)}
                  className={`${hoverColor} transition-colors`}
                >
                  {t.imprint}
                </a>
              </li>
              <li>
                <a
                  href="mailto:neosearch@neobim.eu"
                  className={`${hoverColor} transition-colors`}
                >
                  {t.contact}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className={`pt-8 border-t ${borderColor} text-center ${mutedColor}`}>
          <p className={`text-sm ${mutedColor} max-w-2xl mx-auto mb-4`}>
            {t.complianceNote}
          </p>
          <p>
            {t.productLine}{" "}
            <a
              href="https://www.neobim.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold text-slate-200 hover:text-orange-400 transition-colors"
            >
              neoBIM
            </a>
            .
          </p>
          <p className="mt-2">{t.copyright}</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;